// Device list pure helpers: command/status interpretation and request row parsing.

import type { DeviceStatus } from '../../../../types/DeviceListTypes';
import {
  fleetStatusAliasIds,
  isFrontForcedOfflineByRearRace,
} from '../../../helpers/devicePairing';

/** Normalize numeric Dynamo fields that may arrive as strings over JSON/WS. */
export function normalizeDeviceStatus(status: DeviceStatus): DeviceStatus {
  const raw = status as Record<string, unknown>;
  const next: Record<string, unknown> = { ...raw };

  if (next.race_mode != null && next.race_mode !== '') {
    const rm = Number(next.race_mode);
    next.race_mode = Number.isFinite(rm) ? rm : 0;
  }
  if (next.busy != null && next.busy !== '') {
    const busy = Number(next.busy);
    next.busy = Number.isFinite(busy) ? busy : 0;
  }
  if (next.updatedAt != null && next.updatedAt !== '') {
    const u = Number(next.updatedAt);
    if (Number.isFinite(u) && u > 0) next.updatedAt = u;
  }

  return next as DeviceStatus;
}

/** True if command status means the device is actively processing (so we treat it as "online" for display). */
export function isCommandActive(status?: string): boolean {
  if (!status) return false;
  const s = status.toUpperCase();
  return s === 'IN_PROGRESS' || s === 'ACKED' || s === 'PENDING';
}

/** Milliseconds from a device status updatedAt field (sec or ms epoch). */
export function toStatusSeenMs(status?: DeviceStatus): number {
  const raw = status?.updatedAt as unknown;
  let updatedAt: number | undefined;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    updatedAt = raw;
  } else if (typeof raw === 'string' && raw.trim()) {
    const parsed = Number(raw.trim());
    if (Number.isFinite(parsed) && parsed > 0) updatedAt = parsed;
  }
  if (updatedAt == null || updatedAt <= 0) return 0;
  return updatedAt > 1e12 ? updatedAt : updatedAt * 1000;
}

/** Latest heartbeat ms for a row, checking canonical and legacy status keys. */
export function bestFleetStatusHeartbeatMs(
  deviceId: string,
  statusMap: Record<string, DeviceStatus | undefined>,
  lastStatusSeen: Record<string, number>,
): number {
  let best = 0;
  for (const key of fleetStatusAliasIds(deviceId)) {
    best = Math.max(best, lastStatusSeen[key] ?? 0, toStatusSeenMs(statusMap[key]));
  }
  return best;
}

/** Per-row online: heartbeat, active command, and front-offline-while-rear-races rule. */
export function isFleetDeviceOnline(params: {
  deviceId: string;
  statusMap: Record<string, DeviceStatus | undefined>;
  lastStatusSeen: Record<string, number>;
  now: number;
  commandStatus?: string;
  onlineTimeoutMs: number;
}): boolean {
  const deviceId = String(params.deviceId ?? '').trim();
  if (!deviceId) return false;
  if (isFrontForcedOfflineByRearRace(deviceId, params.statusMap)) return false;

  // Front devices are only reachable over the rear's BLE link. They are online ONLY
  // when the rear has explicitly proxied front_link_status === 'CONNECTED'. A missing,
  // empty, or non-CONNECTED value means no live front block → treat as offline (don't
  // wait for updatedAt staleness, and don't let pair-mirroring infer online).
  if (deviceId.toLowerCase().startsWith('front_')) {
    const frontStatus = params.statusMap[deviceId];
    const linkStatus = String(frontStatus?.front_link_status ?? '').trim().toUpperCase();
    if (linkStatus !== 'CONNECTED') return false;
  }

  let heartbeatOnline = false;
  for (const key of fleetStatusAliasIds(deviceId)) {
    const lastSeen = Math.max(
      params.lastStatusSeen[key] ?? 0,
      toStatusSeenMs(params.statusMap[key]),
    );
    if (lastSeen >= params.now - params.onlineTimeoutMs) {
      heartbeatOnline = true;
      break;
    }
  }

  return heartbeatOnline || isCommandActive(params.commandStatus);
}

/** Normalize command/command-table updatedAt to milliseconds. */
export function toCommandUpdatedMs(updatedAt?: number): number {
  if (typeof updatedAt !== 'number' || !Number.isFinite(updatedAt) || updatedAt <= 0) return 0;
  return updatedAt > 1e12 ? updatedAt : updatedAt * 1000;
}

/**
 * Busy when the backend status row has busy set, or when a command is active.
 * Device telemetry can clear the DynamoDB busy flag mid-upload (busy_from ≤ updatedAt
 * rule), so we also hold busy for the full active command lifecycle via cmdStatus.
 */
export function isDeviceBusy(
  status?: DeviceStatus,
  cmdStatus?: string,
  _lastStatusSeenMs?: number,
  _latestCommandUpdatedAt?: number,
): boolean {
  if (isBusyFromStatus(status)) return true;
  return isCommandActive(cmdStatus);
}

/** Strip `[device]: '...'` wrappers from firmware reason strings. */
export function stripDeviceReasonWrapper(raw: string): string {
  const text = String(raw ?? '').trim();
  if (!text) return '';
  const singleQuoted = /\[device\]:\s*'([^']*)'/i.exec(text);
  if (singleQuoted?.[1]) return singleQuoted[1].trim();
  const doubleQuoted = /\[device\]:\s*"([^"]*)"/i.exec(text);
  if (doubleQuoted?.[1]) return doubleQuoted[1].trim();
  return text;
}

/** Last-cmd feedback column — prefers real-time ACK-sourced feedback, falls back to device pulse. */
export function formatDeviceFeedbackReason(status?: DeviceStatus): string {
  const ackFeedback = String(status?.last_cmd_feedback ?? '').trim();
  if (ackFeedback) return ackFeedback;
  return stripDeviceReasonWrapper(String(status?.last_state_reason ?? ''));
}

/** Derive busy flag from status (busy, isBusy, or mode === 'busy'). */
export function isBusyFromStatus(status?: DeviceStatus): boolean {
  const s = status as any;
  if (!s || typeof s !== 'object') return false;

  if (Object.prototype.hasOwnProperty.call(s, 'busy')) {
    const v = s.busy;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v === 1;
    if (typeof v === 'string') {
      const t = v.trim().toLowerCase();
      if (t === '1' || t === 'true' || t === 'busy') return true;
      if (t === '0' || t === 'false' || t === 'idle') return false;
    }
  }

  if (Object.prototype.hasOwnProperty.call(s, 'isBusy')) {
    const v = s.isBusy;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v === 1;
    if (typeof v === 'string') {
      const t = v.trim().toLowerCase();
      if (t === '1' || t === 'true') return true;
      if (t === '0' || t === 'false') return false;
    }
  }

  const mode =
    typeof s.mode === 'string'
      ? s.mode
      : typeof s.appMode === 'string'
        ? s.appMode
        : typeof s.app_mode === 'string'
          ? s.app_mode
          : undefined;

  if (typeof mode === 'string' && mode.trim().toLowerCase() === 'busy') return true;

  return false;
}

import type { RequestRowLike } from '../types/DeviceListViewTypes';

export type { RequestRowLike };

/** Parse a raw request row from API into requestId, deviceIds, type, createdAt. */
export function parseRequestRow(
  row: RequestRowLike,
): { requestId: string; deviceIds: string[]; type?: string; createdAt?: number } | null {
  let body: Record<string, unknown> = {};

  const bodyJson = row?.bodyJson;
  if (typeof bodyJson === 'string') {
    try {
      const parsed = JSON.parse(bodyJson) as unknown;
      if (parsed && typeof parsed === 'object') body = parsed as Record<string, unknown>;
    } catch {
      body = {};
    }
  } else if (bodyJson && typeof bodyJson === 'object') {
    body = bodyJson as Record<string, unknown>;
  }

  const requestId =
    typeof row?.requestId === 'string'
      ? row.requestId.trim()
      : typeof row?.id === 'string'
        ? row.id.trim()
        : typeof body?.requestId === 'string'
          ? body.requestId.trim()
          : '';

  const deviceIdsSrc =
    Array.isArray(body?.deviceIds) ? body.deviceIds
      : Array.isArray(row?.deviceIds) ? row.deviceIds
        : [];

  const deviceIds =
    Array.isArray(deviceIdsSrc)
      ? deviceIdsSrc.map((x) => String(x ?? '').trim()).filter(Boolean)
      : [];

  if (!requestId) return null;

  const type =
    typeof body?.type === 'string'
      ? body.type
      : typeof row?.type === 'string'
        ? row.type
        : undefined;

  const createdAt =
    typeof row?.createdAt === 'number'
      ? row.createdAt
      : typeof body?.createdAt === 'number'
        ? body.createdAt
        : undefined;

  return { requestId, deviceIds, type, createdAt };
}
